// Implements: F-IDN-027
/**
 * The role vocabulary of the organization pages, as DATA for the selectors (Default P3 plan,
 * Global Constraints): the options each select offers and the dictionary key of each label.
 *
 * Nothing here decides what a person may see or do. Gating follows `ScopeEntry.permissions` only;
 * a role literal appears in this file and nowhere in a condition that shows or hides something.
 */

import type { InvitationRole, OrgRole, TeamRole } from "../../../types/organization";

export type RoleLabelKey =
  | "organization.role.orgAdmin"
  | "organization.role.orgMember"
  | "organization.role.teamLead"
  | "organization.role.teamMember";

export type TeamRoleLabelKey = "organization.teamRole.lead" | "organization.teamRole.member";

/** The organization roles, in the order the Personas select offers them. */
export const ORG_ROLES: readonly OrgRole[] = ["org_admin", "org_member"];

/** The team roles, in the order the team member select offers them. */
export const TEAM_ROLES: readonly TeamRole[] = ["team_lead", "team_member"];

/** The roles an invitation may carry, in the v3 invite dialog's order. */
export const INVITATION_ROLES: readonly InvitationRole[] = ["org_member", "org_admin", "team_member", "team_lead"];

/** The role a team member is added with unless the person picks another. */
export const DEFAULT_TEAM_ROLE: TeamRole = "team_member";

/** The role an invitation starts with. */
export const DEFAULT_INVITATION_ROLE: InvitationRole = "org_member";

/** The organization role a team invitation carries: the team seat travels apart, as `team_role`. */
export const TEAM_INVITATION_ORG_ROLE: OrgRole = "org_member";

/** What one choice of the invite dialog's Rol select sends (owner 2026-10-18, option B). */
export interface InvitationSeat {
  readonly role: OrgRole;
  readonly teamId: string | null;
  readonly teamRole: TeamRole | null;
}

/** A team role becomes an organization member seated in `teamId` with that team role; an org role sends no team. */
export function invitationSeat(choice: InvitationRole, teamId: string): InvitationSeat {
  if (isTeamRole(choice)) return { role: TEAM_INVITATION_ORG_ROLE, teamId, teamRole: choice };
  return { role: choice, teamId: null, teamRole: null };
}

/** The label of an invitation's Rol cell: its team role when it carries one, its organization role otherwise. */
export function invitationRoleLabelKey(invitation: { readonly role: OrgRole; readonly team_role: TeamRole | null }): RoleLabelKey {
  return roleLabelKey(invitation.team_role ?? invitation.role);
}

const ROLE_LABELS: Readonly<Record<InvitationRole, RoleLabelKey>> = {
  org_admin: "organization.role.orgAdmin",
  org_member: "organization.role.orgMember",
  team_lead: "organization.role.teamLead",
  team_member: "organization.role.teamMember",
};

const TEAM_ROLE_LABELS: Readonly<Record<TeamRole, TeamRoleLabelKey>> = {
  team_lead: "organization.teamRole.lead",
  team_member: "organization.teamRole.member",
};

export function roleLabelKey(role: InvitationRole): RoleLabelKey {
  return ROLE_LABELS[role];
}

export function teamRoleLabelKey(role: TeamRole): TeamRoleLabelKey {
  return TEAM_ROLE_LABELS[role];
}

/** Whether an invitation's role places the person in a team (so the team is required). */
export function isTeamRole(role: InvitationRole): role is TeamRole {
  return (TEAM_ROLES as readonly InvitationRole[]).includes(role);
}

/** Whether the person leads the team (the "· líder" badge, "Líder: …" line). */
export function isLead(role: TeamRole): boolean {
  return role === "team_lead";
}

/** Whether the seat administers the organization (the "N admins" count). */
export function isOrgAdmin(role: OrgRole | undefined): boolean {
  return role === "org_admin";
}

/** A select's raw value read back as one of `allowed`, or `null` for anything else. */
export function asRole<R extends string>(value: string, allowed: readonly R[]): R | null {
  return (allowed as readonly string[]).includes(value) ? (value as R) : null;
}
